import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Sparkles } from 'lucide-react';

export const Route = createFileRoute('/dziekujemy')({
  validateSearch: (s) =>
    ({ draft: typeof s.draft === 'string' ? s.draft : undefined }) as {
      draft?: string;
    },
  component: ThankYouPage,
});

const API = import.meta.env.VITE_API_BASE_URL ?? '';

interface StatusResponse {
  draft_state: string;
  tenant_status?: string;
  tenant_tier?: string;
  preview_token?: string;
  slug: string;
  build_status: 'requested' | 'building' | 'ready' | 'failed' | null;
  preview_url: string | null;
}

function ThankYouPage() {
  const { draft } = Route.useSearch();
  const status = useQuery({
    queryKey: ['onboarding-status', draft],
    enabled: !!draft,
    queryFn: async (): Promise<StatusResponse> => {
      const res = await fetch(`${API}/api/public/onboarding/${draft}/status`);
      if (!res.ok) throw new Error('status_failed');
      return (await res.json()) as StatusResponse;
    },
    refetchInterval: (q) => {
      const data = q.state.data as StatusResponse | undefined;
      if (!data) return 5_000;
      if (data.build_status === 'ready' || data.build_status === 'failed') return false;
      return 5_000;
    },
  });

  const data = status.data;
  const isReady = data?.build_status === 'ready';
  const isFailed = data?.build_status === 'failed';
  // While DNS for *.pozyskajpacjenta.pl isn't on our CF account, the
  // canonical preview_url from the api won't resolve. Route through the
  // api-worker's R2 proxy so the dev URL works end-to-end.
  const safePreviewUrl =
    isReady && data?.slug ? `${API}/api/public/preview/${data.slug}/` : data?.preview_url ?? '#';

  return (
    <section className="px-6 py-20 md:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success ring-1 ring-success/20">
          <CheckCircle2 className="size-3.5" aria-hidden="true" />
          Zamówienie przyjęte
        </span>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-text md:text-5xl">
          Dziękujemy!
        </h1>
        <p className="mt-4 text-lg text-text-muted">
          Pracujemy nad podglądem Twojej strony. Otrzymasz e-mail z linkiem
          w ciągu maksymalnie 24 godzin (zwykle szybciej — ~3 minut na samą generację,
          plus operator robi szybki przegląd).
        </p>
        <p className="mt-2 text-sm text-text-subtle">
          Nic teraz nie płacisz. Decyzję podejmiesz po obejrzeniu strony.
        </p>

        <div className="mt-12 rounded-2xl bg-bg p-6 ring-1 ring-border">
          {!data ? (
            <p className="text-sm text-text-muted">Ładuję status…</p>
          ) : isReady ? (
            <div className="space-y-4">
              <p className="font-display text-lg font-semibold text-success">
                Strona gotowa!
              </p>
              <a
                href={safePreviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-text px-6 py-3 text-base font-medium text-bg hover:bg-text/90"
              >
                Zobacz podgląd <ExternalLink className="size-4" aria-hidden="true" />
              </a>
              <p className="text-xs text-text-subtle">
                Otrzymasz też e-mail z linkiem do panelu admin.
              </p>
            </div>
          ) : isFailed ? (
            <div className="space-y-3">
              <p className="font-display text-lg font-semibold text-error">
                Coś poszło nie tak
              </p>
              <p className="text-sm text-text-muted">
                Skontaktuj się z nami na kontakt@pozyskajpacjenta.pl — zwrócimy płatność lub spróbujemy ponownie.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent ring-1 ring-accent/20">
                <Sparkles className="size-3.5 animate-pulse" aria-hidden="true" />
                Pracujemy nad Twoją stroną…
              </span>
              <ul className="mx-auto max-w-md space-y-2 text-left text-sm">
                <StepLine done label="Konto kliniki utworzone" />
                <StepLine done label="Brief przyjęty" />
                <StepLine
                  done={data.build_status === 'building' || data.build_status === 'ready'}
                  active={data.build_status === 'building'}
                  label="Tworzenie treści strony"
                />
                <StepLine
                  done={isReady}
                  active={!isReady}
                  label="Publikacja do sieci"
                />
                <StepLine done={isReady} label="Strona publicznie dostępna" />
              </ul>
              <p className="text-xs text-text-subtle">
                Status odświeża się automatycznie co 5 sekund.
              </p>
            </div>
          )}
        </div>

        <p className="mt-8 text-xs text-text-subtle">
          {data?.slug && (
            <>
              Nie czekasz? Otwórz{' '}
              <a
                href={`${API}/api/public/preview/${data.slug}/`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[11px] text-accent hover:underline"
              >
                podgląd
              </a>{' '}
              za parę minut.
            </>
          )}
        </p>
      </div>
    </section>
  );
}

function StepLine({
  done,
  active,
  label,
}: {
  done?: boolean;
  active?: boolean;
  label: string;
}) {
  return (
    <li className="flex items-center gap-3">
      <span
        className={
          done
            ? 'grid size-5 place-items-center rounded-full bg-success text-bg'
            : active
            ? 'grid size-5 place-items-center rounded-full bg-accent text-bg animate-pulse'
            : 'grid size-5 place-items-center rounded-full bg-surface text-text-subtle ring-1 ring-border'
        }
      >
        {done ? <CheckCircle2 className="size-3.5" aria-hidden="true" /> : null}
      </span>
      <span className={done ? 'text-text' : active ? 'text-text' : 'text-text-subtle'}>
        {label}
      </span>
    </li>
  );
}
